import type { Config } from 'tailwindcss';

// Default config is on
// https://tailwindcss.nuxtjs.org/tailwind/config#default-configuration
// https://github.com/tailwindlabs/tailwindcss/blob/master/stubs/config.full.js

// CSS variables are in shared.scss

export default <Partial<Config>>{
  safelist: [
    {
      pattern: /pt-\d+/,
      variants: ['md', 'lg'],
    },
    {
      pattern: /pb-\d+/,
      variants: ['md', 'lg'],
    },
    ...Array.from({ length: 101 }, (_, i) => {
      const widthValue = `w-[${i}dvw]`;
      return [
        widthValue,
        `sm:${widthValue}`,
        `md:${widthValue}`,
        `xl:${widthValue}`,
        `2xl:${widthValue}`,
        `3xl:${widthValue}`,
      ];
    }).flat(),
    ...Array.from({ length: 101 }, (_, i) => {
      const widthValue = `w-[${i}rem]`;
      return [
        widthValue,
        `sm:${widthValue}`,
        `md:${widthValue}`,
        `xl:${widthValue}`,
        `2xl:${widthValue}`,
        `3xl:${widthValue}`,
      ];
    }).flat(),
  ],
  darkMode: [
    'variant',
    [
      '&:where(.dark-theme, .dark-theme *)',
      '&:where(.blackweek-theme, .blackweek-theme *)',
    ],
  ],
  content: [
    './components/**/*.{vue,js,ts}',
    './composables/**/*.{vue,js,ts}',
    './layouts/**/*.{vue,js,ts}',
    './pages/**/*.{vue,js,ts}',
    './utils/**/*.{vue,js,ts}',
    './app.vue',
  ],
  theme: {
    colors: {
      inherit: 'inherit',
      'blue-50': 'var(--blue-50)',
      'blue-100': 'var(--blue-100)',
      'blue-200': 'var(--blue-200)',
      'blue-500': 'var(--blue-500)',
      'blue-700': 'var(--blue-700)',
      'blue-900': 'var(--blue-900)',
      'blue-no-name': 'var(--blue-no-name)',
      'white-50': 'var(--white-50)',
      'white-500': 'var(--white-500)',
      'green-50': 'var(--green-50)',
      'green-500': 'var(--green-500)',
      'green-600': 'var(--green-600)',
      'green-700': 'var(--green-700)',
      'green-800': 'var(--green-800)',
      'yellow-500': 'var(--yellow-500)',
      'orange-500': 'var(--orange-500)',
      'red-300': 'var(--red-300)',
      'red-400': 'var(--red-400)',
      'red-500': 'var(--red-500)',
      'grey-200': 'var(--grey-200)',
      'grey-300': 'var(--grey-300)',
      'grey-400': 'var(--grey-400)',
      'grey-500': 'var(--grey-500)',
      //temp name
      'card-black-shadow': 'var(--card-black-shadow)',

      'text-primary': 'var(--text-primary)',
      'text-primary-inverted': 'var(--text-primary-inverted)',
      'text-secondary': 'var(--text-secondary)',
      'text-link': 'var(--text-link)',
      'text-link-hover': 'var(--text-link-hover)',
      'text-disabled': 'var(--text-disabled)',
      'text-on-action': 'var(--text-on-action)',
      'text-on-action-hover': 'var(--text-on-action-hover)',
      'text-on-highlight': 'var(--text-on-highlight)',
      'text-success': 'var(--text-success)',
      'text-warning': 'var(--text-warning)',
      'text-error': 'var(--text-error)',
      'surface-page': 'var(--surface-page)',
      'surface-primary': 'var(--surface-primary)',
      'surface-secondary': 'var(--surface-secondary)',
      'surface-tertiary': 'var(--surface-tertiary)',
      'surface-success': 'var(--surface-success)',
      'surface-error': 'var(--surface-error)',
      'surface-warning': 'var(--surface-warning)',
      'surface-information': 'var(--surface-information)',
      'surface-highlight': 'var(--surface-highlight)',
      'surface-action-1': 'var(--surface-action-1)',
      'surface-action-hover': 'var(--surface-action-hover)',
      'surface-disabled': 'var(--surface-disabled)',
      'surface-action-2': 'var(--surface-action-2)',
      'surface-modal': 'var(--surface-modal)',
      'border-primary': 'var(--border-primary)',
      'border-secondary': 'var(--border-secondary)',
      'border-tertiary': 'var(--border-tertiary)',
      'border-information': 'var(--border-information)',
      'border-success': 'var(--border-success)',
      'border-warning': 'var(--border-warning)',
      'border-error': 'var(--border-error)',
      'border-highlight': 'var(--border-highlight)',
      'border-disabled': 'var(--border-disabled)',
      'border-hover': 'var(--border-hover)',
      'icon-primary': 'var(--icon-primary)',
      'icon-secondary': 'var(--icon-secondary)',
      'icon-tertiary': 'var(--icon-tertiary)',
      'icon-success': 'var(--icon-success)',
      'icon-error': 'var(--icon-error)',
      'icon-warning': 'var(--icon-warning)',
      'icon-link': 'var(--icon-link)',
      'transparent-black-800': 'var(--transparent-black-800)',
      'transparent-black-400': 'var(--transparent-black-400)',
      'transparent-black-300': 'var(--transparent-black-300)',
      'transparent-black-150': 'var(--transparent-black-150)',
      'transparent-white-600': 'var(--transparent-white-600)',
      'transparent-white-500': 'var(--transparent-white-500)',
      'transparent-white-300': 'var(--transparent-white-300)',
      'transparent-white-150': 'var(--transparent-white-150)',

      'label-campaign': 'var(--label-campaign)',
      'label-popular': 'var(--label-popular)',
      'label-news': 'var(--label-news)',
      'label-few-left': 'var(--label-few-left)',
      'label-black': 'var(--label-black)',
      'label-done': 'var(--label-done)',

      'outer-hard-shadow': 'var(--outer-hard-shadow)',
      'inner-hard-shadow': 'var(--inner-hard-shadow)',
      'btm-soft-shadow': 'var(--btm-soft-shadow)',
      'card-blue-shadow': 'var(--card-blue-shadow)',
      'overlay-default': 'var(--overlay-default)',

      //old label colors name
      'label-hot-items': 'var(--orange-500)',
      'label-urgency': 'var(--orange-500)',
      'label-deals': 'var(--orange-500)',
      'label-discounts': 'var(--orange-500)',
      'label-limited-drop': 'var(--grey-500)',
      'label-brand-words': 'var(--yellow-500)',
      'label-specials': 'var(--orange-500)',
      'label-stock': 'var(--orange-500)',
      //old label colors name
      transparent: 'transparent',
    },
    fontSize: {
      14: '1.4rem',
      16: '1.6rem',
      18: '1.8rem',
      20: '2rem',
      22: '2.2rem',
      24: '2.4rem',
      '60-60': ['6rem', { lineHeight: '6rem' }],
      '54-54': ['5.4rem', { lineHeight: '5.4rem' }],
      '52-52': ['5.2rem', { lineHeight: '5.2rem' }],
      '48-48': ['4.8rem', { lineHeight: '4.8rem' }],
      '44-44': ['4.4rem', { lineHeight: '4.4rem' }],
      '42-42': ['4.2rem', { lineHeight: '4.2rem' }],
      '36-36': ['3.6rem', { lineHeight: '3.6rem' }],
      '32-32': ['3.2rem', { lineHeight: '3.2rem' }],
      '30-30': ['3rem', { lineHeight: '3rem' }],
      '28-28': ['2.8rem', { lineHeight: '2.8rem' }],
      '24-26': ['2.4rem', { lineHeight: '2.6rem' }],
      '20-24': ['2.0rem', { lineHeight: '2.4rem' }],
      '18-22': ['1.8rem', { lineHeight: '2.2rem' }],
      '16-22': ['1.6rem', { lineHeight: '2.2rem' }],
      '14-18': ['1.4rem', { lineHeight: '1.8rem' }],
    },
    lineHeight: {
      16: '1.6rem',
      18: '1.8rem',
      20: '2rem',
      22: '2.2rem',
      24: '2.4rem',
      26: '2.6rem',
      28: '2.8rem',
      30: '3rem',
      34: '3.4rem',
    },
    fontWeight: {
      'weight-normal': '400',
    },
    extend: {
      zIndex: {
        base: '0',
        raised: '10',
        header: '20',
        sticky: '30',
        overlay: '40',
        modal: '50',
        toast: '60',
      },
      transitionProperty: {
        height: 'height',
      },
      variants: {
        height: ['responsive', 'hover', 'focus'],
      },
      animation: {
        'spin-slow-ease': 'spin 1.2s ease-in-out infinite',
      },
      backgroundImage: {
        'plan-100':
          "url('https://d2qcocilg0cqfe.cloudfront.net/images/subscriptions/100_card_bg.svg')",
        'plan-30':
          "url('https://d2qcocilg0cqfe.cloudfront.net/images/subscriptions/30_card_bg.svg')",
        'plan-8':
          "url('https://d2qcocilg0cqfe.cloudfront.net/images/subscriptions/8_card_bg.svg')",
        'plan-5':
          "url('https://d2qcocilg0cqfe.cloudfront.net/images/subscriptions/5_card_bg.svg')",
        'plan-3':
          "url('https://d2qcocilg0cqfe.cloudfront.net/images/subscriptions/3_card_bg.svg')",
        'plan-family':
          "url('https://d2qcocilg0cqfe.cloudfront.net/images/subscriptions/family_card_bg.svg')",
        'surface-background': 'var(--surface-background)',
        'seasonal-promotion-card':
          'radial-gradient(92.62% 118.06% at 116.49% 121.29%, rgba(255, 255, 255, 0.50) 0%, rgba(158, 220, 220, 0.50) 100%)',
        'seasonal-promotion-card-hover':
          'radial-gradient(92.62% 118.06% at 116.49% 121.29%, rgba(255, 255, 255, 0.30) 0%, rgba(158, 220, 220, 0.30) 100%)',
        'seasonal-promotion-card-dark':
          'linear-gradient(180deg, #000 0.08%, #092326 23.29%, #52BDB6 121.62%), linear-gradient(130deg, rgba(255, 255, 255, 0.5) 0%, rgba(0, 0, 0, 0.15) 20%, rgba(255, 255, 255, 0.0) 100%)',
        'seasonal-promotion-card-dark-hover':
          'linear-gradient(180deg, rgba(0, 0, 0, 0.20) 0.08%, rgba(9, 35, 38, 0.20) 23.29%, rgba(82, 189, 182, 0.20) 121.62%), linear-gradient(130deg, rgba(255, 255, 255, 0.15) 0%, rgba(0, 0, 0, 0.15) 20%, rgba(255, 255, 255, 0.0) 100%)',
      },
      boxShadow: {
        DEFAULT: '0rem -1.6rem 6.4rem 0rem rgba(0, 0, 0, 0.3)',
        input: 'inset 0.1rem 0.1rem 0 0.1rem var(--inner-hard-shadow)',
        button: '0.1rem 0.1rem 0 var(--outer-hard-shadow)',
        buttonSoft: '0.3rem 0.3rem 0rem 0rem rgba(39, 42, 47, 0.1)',
        buttonSoftWider:
          '0 6px 10px 4px var(--transparent-black-150), 0 2px 3px 0 var(--transparent-black-300)',
        buttonBlue: '0.1rem 0.1rem 0 var(--blue-500)',
        card: '0 0.2rem 0 0 var(--card-black-shadow)',
        infoBox: 'inset 0.6rem 0rem 0 0 var(--blue-500)',
      },
      outlineOffset: {
        input: '0.4rem',
      },
      screens: {
        sm: '320px',
        md: '600px',
        lg: '1024px',
        xl: '1400px',
        '2xl': '1440px',
        '3xl': '2048px',
      },
      borderRadius: {
        0: '0px',
        xs: '0.2rem',
        sm: '0.4rem',
        md: '0.8rem',
        DEFAULT: '0.8rem',
        lg: '1.6rem',
      },
      borderWidth: {
        DEFAULT: '1px',
        sm: '1px',
        md: '2px',
        lg: '4px',
        0: '0',
        1: '1px',
        2: '2px',
        3: '3px',
        4: '4px',
        6: '6px',
        8: '8px',
      },
      fontFamily: {
        medium: ['halebopmedium', 'halebopmedium fallback'],
        light: ['haleboplight', 'haleboplight fallback'],
        xtrabold: ['halebopxtrabold', 'halebopxtrabold fallback'],
      },
      spacing: {
        0: '0px',
        px: '1px',
        0.5: '0.05rem',
        1: '0.1rem',
        1.5: '0.15rem',
        2: '0.2rem',
        2.5: '0.25rem',
        3: '0.3rem',
        3.5: '0.35rem',
        4: '0.5rem',
        5: '0.5rem',
        6: '0.6rem',
        7: '0.7rem',
        8: '0.8rem',
        9: '0.9rem',
        10: '1rem',
        11: '1.1rem',
        12: '1.2rem',
        13: '1.3rem',
        14: '1.4rem',
        15: '1.5rem',
        16: '1.6rem',
        17: '1.7rem',
        18: '1.8rem',
        19: '1.9rem',
        20: '2rem',
        21: '2.1rem',
        22: '2.2rem',
        23: '2.3rem',
        24: '2.4rem',
        25: '2.5rem',
        26: '2.6rem',
        27: '2.7rem',
        28: '2.8rem',
        29: '2.9rem',
        30: '3rem',
        31: '3.1rem',
        32: '3.2rem',
        33: '3.3rem',
        34: '3.4rem',
        35: '3.5rem',
        36: '3.6rem',
        37: '3.7rem',
        38: '3.8rem',
        39: '3.9rem',
        40: '4rem',
        41: '4.1rem',
        42: '4.2rem',
        43: '4.3rem',
        44: '4.4rem',
        45: '4.5rem',
        46: '4.6rem',
        47: '4.7rem',
        48: '4.8rem',
        49: '4.9rem',
        50: '5rem',
        51: '5.1rem',
        52: '5.2rem',
        53: '5.3rem',
        54: '5.4rem',
        55: '5.5rem',
        56: '5.6rem',
        57: '5.7rem',
        58: '5.8rem',
        59: '5.9rem',
        60: '6rem',
        61: '6.1rem',
        62: '6.2rem',
        63: '6.3rem',
        64: '6.4rem',
        65: '6.5rem',
        66: '6.6rem',
        67: '6.7rem',
        68: '6.8rem',
        69: '6.9rem',
        70: '7rem',
        71: '7.1rem',
        72: '7.2rem',
        73: '7.3rem',
        74: '7.4rem',
        75: '7.5rem',
        76: '7.6rem',
        77: '7.7rem',
        78: '7.8rem',
        79: '7.9rem',
        80: '8rem',
        81: '8.1rem',
        82: '8.2rem',
        83: '8.3rem',
        84: '8.4rem',
        85: '8.5rem',
        86: '8.6rem',
        87: '8.7rem',
        88: '8.8rem',
        89: '8.9rem',
        90: '9rem',
        91: '9.1rem',
        92: '9.2rem',
        93: '9.3rem',
        94: '9.4rem',
        95: '9.5rem',
        96: '9.6rem',
        97: '9.7rem',
        98: '9.8rem',
        99: '9.9rem',
        100: '10rem',
        101: '10.1rem',
        102: '10.2rem',
        103: '10.3rem',
        104: '10.4rem',
        105: '10.5rem',
        106: '10.6rem',
        107: '10.7rem',
        108: '10.8rem',
        109: '10.9rem',
        110: '11rem',
        111: '11.1rem',
        112: '11.2rem',
        113: '11.3rem',
        114: '11.4rem',
        115: '11.5rem',
        116: '11.6rem',
        117: '11.7rem',
        118: '11.8rem',
        119: '11.9rem',
        120: '12rem',
        121: '12.1rem',
        122: '12.2rem',
        123: '12.3rem',
        124: '12.4rem',
        125: '12.5rem',
        126: '12.6rem',
        127: '12.7rem',
        128: '12.8rem',
        129: '12.9rem',
        130: '13rem',
        131: '13.1rem',
        132: '13.2rem',
        133: '13.3rem',
        134: '13.4rem',
        135: '13.5rem',
        136: '13.6rem',
        137: '13.7rem',
        138: '13.8rem',
        139: '13.9rem',
        140: '14rem',
        141: '14.1rem',
        142: '14.2rem',
        143: '14.3rem',
        144: '14.4rem',
        145: '14.5rem',
        146: '14.6rem',
        147: '14.7rem',
        148: '14.8rem',
        149: '14.9rem',
        150: '15rem',
        151: '15.1rem',
        152: '15.2rem',
        153: '15.3rem',
        154: '15.4rem',
        155: '15.5rem',
        156: '15.6rem',
        157: '15.7rem',
        158: '15.8rem',
        159: '15.9rem',
        160: '16rem',
        161: '16.1rem',
        162: '16.2rem',
        163: '16.3rem',
        164: '16.4rem',
        165: '16.5rem',
        166: '16.6rem',
        167: '16.7rem',
        168: '16.8rem',
        169: '16.9rem',
        170: '17rem',
        171: '17.1rem',
        172: '17.2rem',
        173: '17.3rem',
        174: '17.4rem',
        175: '17.5rem',
        176: '17.6rem',
        177: '17.7rem',
        178: '17.8rem',
        179: '17.9rem',
        180: '18rem',
        181: '18.1rem',
        182: '18.2rem',
        183: '18.3rem',
        184: '18.4rem',
        185: '18.5rem',
        186: '18.6rem',
        187: '18.7rem',
        188: '18.8rem',
        189: '18.9rem',
        190: '19rem',
        191: '19.1rem',
        192: '19.2rem',
        193: '19.3rem',
        194: '19.4rem',
        195: '19.5rem',
        196: '19.6rem',
        197: '19.7rem',
        198: '19.8rem',
        199: '19.9rem',
        200: '20rem',
        300: '30rem',
        400: '40rem',
        420: '42rem',
        447: '44.7rem',
        474: '47.4rem',
        480: '48rem',
        546: '54.6rem',
        600: '60rem',
        660: '66rem',
        728: '72.8rem',
        720: '72rem',
        770: '77rem',
      },
    },
  },
};
